import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { logout } from '../store/user/actions';
import storageService from '../services/storageService';

const STORAGE_KEY = 'lastActivityAt';
const CHANNEL = 'app-activity';

export default function IdleLogoutProvider({
  timeoutMs = 2 * 60 * 60 * 1000, // 2 hours
  warnMs = 5 * 60 * 1000, // warn 5 minutes before
  children,
  onWarn,
  onCancelWarn,
}) {
  const dispatch = useDispatch();
  const [warned, setWarned] = useState(false);
  const timerRef = useRef(null);
  const bcRef = useRef(null);

  const now = () => Date.now();

  const writeActivity = (ts) => {
    storageService.setItem(STORAGE_KEY, String(ts));
    try {
      bcRef.current?.postMessage({ type: 'activity', ts });
    } catch (e) {}
  };

  const getLastActivity = () => {
    const v = storageService.getItem(STORAGE_KEY);
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const scheduleChecks = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(check, 30_000);
  };

  const markActive = () => {
    if (warned) {
      setWarned(false);
      onCancelWarn && onCancelWarn();
    }
    writeActivity(now());
    scheduleChecks();
  };

  const handlePossibleActivity = () => {
    const last = getLastActivity();
    if (now() - last > 2000) markActive(); // throttle writes
  };

  const check = () => {
    const last = getLastActivity();
    const idleFor = now() - last;

    if (!warned && idleFor >= timeoutMs - warnMs && idleFor < timeoutMs) {
      setWarned(true);
      onWarn && onWarn();
    }

    if (idleFor >= timeoutMs) {
      console.log("logging out");
      dispatch(logout());
    }
  };

  useEffect(() => {
    // init timestamp if missing
    if (!getLastActivity()) writeActivity(now());
    scheduleChecks();

    // multi-tab sync: BroadcastChannel if available, else storage event
    let fallbackStorageListener = null;

    try {
      bcRef.current = new BroadcastChannel(CHANNEL);
      bcRef.current.onmessage = (e) => {
        if (e?.data?.type === 'activity') {
          if (warned) {
            setWarned(false);
            onCancelWarn && onCancelWarn();
          }
          scheduleChecks();
        }
      };
    } catch (e) {
      fallbackStorageListener = (ev) => {
        if (ev.key === STORAGE_KEY) {
          if (warned) {
            setWarned(false);
            onCancelWarn && onCancelWarn();
          }
          scheduleChecks();
        }
      };
      window.addEventListener('storage', fallbackStorageListener);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      try {
        bcRef.current && bcRef.current.close();
      } catch (e) {}
      if (fallbackStorageListener) {
        window.removeEventListener('storage', fallbackStorageListener);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll', 'focus'];

    const onActivity = () => handlePossibleActivity();
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    const onVis = () => {
      if (document.visibilityState === 'visible') handlePossibleActivity();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warned]);

  return children || null;
}
